import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({
    status: "polling",
    payment_status: null,
    amount_total: 0,
    currency: "usd",
    error: null,
  });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, status: "error", error: "Missing session id" }));
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({
            status: "paid",
            payment_status: "paid",
            amount_total: data.amount_total,
            currency: data.currency,
            error: null,
          });
          return;
        }
        if (data.status === "expired") {
          setState((s) => ({ ...s, status: "expired" }));
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState((s) => ({ ...s, status: "timeout" }));
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState((s) => ({ ...s, status: "error", error: e?.message || "Error" }));
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      className="min-h-screen bg-[#FAF6F0] text-[#2A2521] flex items-center justify-center px-6"
      data-testid="payment-success-page"
    >
      <div className="max-w-lg w-full text-center">
        {state.status === "polling" && (
          <div data-testid="payment-status-polling">
            <Loader2 className="w-10 h-10 text-[#A67C2E] mx-auto mb-6 animate-spin" />
            <p className="label-eyebrow text-[#A67C2E] mb-3">Confirming Payment</p>
            <h1 className="font-heading text-3xl md:text-4xl mb-4">
              One moment…
            </h1>
            <p className="text-[#6B6560] text-sm">
              We&apos;re confirming your payment with Stripe. This usually takes a few seconds.
            </p>
          </div>
        )}

        {state.status === "paid" && (
          <div data-testid="payment-status-paid">
            <CheckCircle2 className="w-12 h-12 text-[#A67C2E] mx-auto mb-6" />
            <p className="label-eyebrow text-[#A67C2E] mb-3">Application Received</p>
            <h1 className="font-heading text-4xl md:text-5xl mb-4 leading-tight">
              Thank you.
            </h1>
            <p className="text-[#6B6560] text-sm mb-2">
              Payment of{" "}
              <span className="text-[#2A2521] font-medium">
                ${(state.amount_total / 100).toFixed(2)}{" "}
                {state.currency?.toUpperCase()}
              </span>{" "}
              has been received.
            </p>
            <p className="text-[#6B6560] text-sm mb-10">
              We correspond within 48 hours of receipt.
            </p>
            <Link
              to="/"
              data-testid="return-home-btn"
              className="btn-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-sm text-sm"
            >
              Return Home <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <div data-testid="payment-status-error">
            <XCircle className="w-12 h-12 text-[#B91C1C] mx-auto mb-6" />
            <h1 className="font-heading text-3xl md:text-4xl mb-4">
              We couldn&apos;t confirm your payment.
            </h1>
            <p className="text-[#6B6560] text-sm mb-8">
              {state.status === "expired"
                ? "The payment session expired."
                : state.status === "timeout"
                ? "This is taking longer than usual. Please check your email for confirmation."
                : state.error || "Something went wrong. Please try again."}
            </p>
            <Link
              to="/apply"
              className="btn-ghost-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-sm text-sm"
            >
              Try again
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
